import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Header } from "../../../components/header/Header";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { getFileUrl } from "../../../utils/GetAuthUser";
import downloadIcon from "../../../assets/download_icon.svg";

import {
  getContestResultThunk as onGetContestResultThunk,
  resultSendToClientThunk as onResultSendToClientThunk,
  resetContestThunk as onResetContestThunk,
  onSaveWinnerRewardThunk as onSaveRewardThunk, // make sure this thunk exists in your slice
} from "../../../slices/contest/thunk";
import { getUserRewardsThunk as onGetUserRewardsThunk } from "../../../slices/users/thunk";
import eyeIcon from "../../../assets/eye_icon.svg";
import { Spinner } from "../../../components/spinner/Spinner";
import { getRole } from "../../../utils/GetRole";


export const ContestResult = () => {

  const fileUrl = getFileUrl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { contestId } = useParams();

  const [loadingId, setLoadingId] = useState(null);
  const [winnerIndex, setWinnerIndex] = useState(-1);
  const [localResults, setLocalResults] = useState([]);

  // Reward popup state
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [selectedWinnerCard, setSelectedWinnerCard] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [rewardLoading, setRewardLoading] = useState(false);

  // Check if current route is user/contest-results
  const isUserContestResults = location.pathname.includes("user/contest-results");
  const API_BASE = process.env.REACT_APP_API_URL;
  const role = getRole();

  const selectContestState = (state) => state.contest;
  const contestSelector = createSelector(selectContestState, (contest) => ({
    isLoading: contest.isLoading,
    contestResult: contest.contestResult,
  }));

  const { contestResult } = useSelector(contestSelector);

  const selectUserState = (state) => state.user;
  const userSelector = createSelector(selectUserState, (user) => ({
    isLoading: user.isLoading,
    userRewards: user.userRewards || [],
  }));

  const { isLoading, userRewards } = useSelector(userSelector);

  // Sync Redux data to localResults when contestResult changes
  useEffect(() => {
    // console.log("Contest Result from Redux:", contestResult);

    if (contestResult && contestResult.length > 0) {
      setLocalResults(contestResult);

      const winnerIdx = contestResult.findIndex((item) => item.is_final === "1");
      setWinnerIndex(winnerIdx);
    }
  }, [contestResult]);

  // Fetch contest results initially
  useEffect(() => {
    dispatch(onResetContestThunk());
    // console.log("Fetching contest results for contestId:", contestId);
    
    dispatch(onGetContestResultThunk({ is_final: null, contestId: contestId }));
    dispatch(onGetUserRewardsThunk({ rewardType: null }));

    // console.log("User Rewards from Redux:", userRewards);
  }, [dispatch]);

  // Send to client handler
  const handleSendToClient = async (id) => {
    try {
      setLoadingId(id);

      // Optimistically update UI immediately
      setLocalResults((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, is_final: "1" } : item
        )
      );

      // Call API
      const result = await dispatch(
        onResultSendToClientThunk({ id, status: 1 })
      ).unwrap();

      if (result?.success || result?.status === true) {
        // Refresh results from server
        await dispatch(onGetContestResultThunk({ is_final: null, contestId: contestId }));
      }
    } catch (error) {
      // console.error("Error sending result to client:", error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleViewProfile = (id) => {
    navigate("/artist/profile-view");
  };

  const handleDownload = (result) => {
    const fileName = result ? result.split("/").pop() : "contest-result.png";
    const link = document.createElement("a");
    link.href = `${API_BASE}/download/${fileName}`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Open reward popup for a specific winner card
  const handleOpenRewardPopup = (card) => {
    setSelectedWinnerCard(card);
    setSelectedReward(null);
    setCustomAmount("");
    setShowRewardPopup(true);
  };

  // Close reward popup
  const handleCloseRewardPopup = () => {
    setShowRewardPopup(false);
    setSelectedWinnerCard(null);
    setSelectedReward(null);
    setCustomAmount("");
  };

  // When admin selects a reward from the list
  const handleSelectReward = (reward) => {
    setSelectedReward(reward);
    // Pre-fill amount if reward type is amount
    if (reward.reward_type === "amount") {
      setCustomAmount(reward.amount || reward.value || "");
    } else {
      setCustomAmount("");
    }
  };

  // Save reward
  const handleSaveReward = async () => {
    if (!selectedReward) return;

    try {
      setRewardLoading(true);

      const payload = {
        contest_id: contestId,
        artist_id: selectedWinnerCard.artist_id,
        
        reward_id: selectedReward.id,
        reward_amount: selectedReward.reward_type === "amount" ? customAmount : null,
        
      };

      // console.log("payload");
      // console.log(payload);

      const result = await dispatch(onSaveRewardThunk(payload)).unwrap();

      // console.log("Reward save result:", result);

      if (result?.success || result?.status === true) {
        // Update local state to reflect rewarded status
        setLocalResults((prev) =>
          prev.map((item) =>
            item.id === selectedWinnerCard.id ? { ...item, is_rewarded: 1 } : item
          )
        );
        handleCloseRewardPopup();
        // Refresh results from server
        await dispatch(onGetContestResultThunk({ is_final: null, contestId: contestId }));
      }
    } catch (error) {
      // console.error("Error saving reward:", error);
    } finally {
      setRewardLoading(false);
    }
  };

  // Determine which cards to show based on role
  // - artist: only show winner card (if declared)
  // - admin / other: show all cards
  const visibleResults =
    role === "artist"
      ? localResults.filter((item) => item.is_final === "1")
      : localResults;

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color ">
        <Header />
      </header>

      {/* Main Section */}
      <div className="w-full mx-auto bg-[#F7F7F7] px-4 sm:px-0">
        <div className="w-full">
          {/* Page Title */}
          <div className="w-full flex items-center justify-start mt-4">
            <span className="font-bold text-black text-[22px] font-grotesk">
              Contest Results
            </span>
          </div>

          {/* Items Grid */}
          <div className="w-full mt-6">
            <div className="w-full mx-auto grid gap-7 grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleResults && visibleResults.length > 0 ? (
                visibleResults.map((card) => (
                  <div
                    key={card.id}
                    className="w-full border border-border_color p-3 rounded-xl bg-white"
                  >
                    {/* Image Box */}
                    <div className="w-full h-[235px] relative">
                      <img
                        alt={card.userName}
                        className="object-cover object-center w-full h-full block rounded-xl"
                        src={`${fileUrl}${card.resultImage}`}
                      />
                      <div className="absolute top-3 right-3">
                        <button
                          onClick={() => handleDownload(`${card.resultImage}`)}
                          className="bg-secondary h-8 p-2 mr-2 rounded-lg"
                        >
                          <img src={downloadIcon} alt="Download" />
                        </button>
                        <button
                          className="bg-secondary h-8 p-2 rounded-lg"
                          onClick={() => window.open(`${fileUrl}${card.resultImage}`, '_blank')}
                        >
                          <img src={eyeIcon} alt="View" />
                        </button>
                      </div>
                    </div>

                    {/* Content Box */}
                    <div className="mt-3">
                      <span className="font-bold text-[16px] text-black font-grotesk block">
                        {card.contestCategory}
                      </span>
                      <span className="text-[13px] text-gray-500 font-grotesk block mt-1">
                        {card.artistName}
                      </span>

                      {/* ── ARTIST ROLE: only winner badge ── */}
                      {role === "artist" && card.is_final === "1" && (
                        <div className="mt-3">
                          <span className="w-full block text-center text-sm font-bold text-white bg-secondary font-grotesk py-3 px-5 rounded-[10px]">
                            🏆 Winner
                          </span>
                        </div>
                      )}

                      {/* ── ADMIN ROLE (or not on user route): choose winner + reward logic ── */}
                      {!isUserContestResults && role !== "artist" && (
                        <div className="flex flex-col items-center gap-2 w-full mt-3">
                          {/* Winner badge or Choose Winner button */}
                          {card.is_final === "1" ? (
                            <button
                              disabled
                              className="w-full text-sm tracking-wide font-bold text-white bg-secondary font-grotesk py-3 px-5 rounded-[10px] cursor-not-allowed"
                            >
                              🏆 Winner
                            </button>
                          ) : winnerIndex === -1 ? (
                            <button
                              onClick={() => handleSendToClient(card.id)}
                              disabled={loadingId === card.id}
                              className="w-full text-sm tracking-wide font-bold text-white bg-primary font-grotesk py-3 px-5 rounded-[10px] hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                            >
                              {loadingId === card.id ? (
                                <>
                                  <Spinner size={18} color="#fff" />
                                  <span>Sending...</span>
                                </>
                              ) : (
                                "Choose as Winner"
                              )}
                            </button>
                          ) : null}

                          {/* Reward section — only for winner card */}
                          {card.is_final === "1" && (
                            <>
                              {card.is_rewarded == 1 ? (
                                <div className="w-full text-center text-sm font-semibold text-green-600 bg-green-50 border border-green-200 py-2 px-4 rounded-[10px]">
                                  ✅ Reward has been granted
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleOpenRewardPopup(card)}
                                  className="w-full text-sm tracking-wide font-bold text-white bg-green-600 font-grotesk py-3 px-5 rounded-[10px] hover:bg-green-700 transition-all"
                                >
                                  🎁 Grant Reward
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center col-span-full text-gray-500">
                  {role === "artist"
                    ? "Winner has not been declared yet."
                    : "No results found."}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      

      {/* ── Reward Popup ── */}
      {showRewardPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
            {/* Close button */}
            <button
              onClick={handleCloseRewardPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ✕
            </button>

            <h2 className="text-[18px] font-bold font-grotesk text-black mb-1">
              Grant Reward
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Select a reward for <span className="font-semibold text-black">{selectedWinnerCard?.artistName}</span>
            </p>

            {/* Reward list */}
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size={24} color="#000" />
              </div>
            ) : userRewards && userRewards.length > 0 ? (
              <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto pr-1">
                {userRewards.map((reward) => (
                  <button
                    key={reward.id}
                  onClick={() => handleSelectReward(reward)}
                    className={`w-full text-left border rounded-xl px-4 py-3 transition-all ${
                      selectedReward?.id === reward.id
                        ? "border-primary bg-primary/5"
                        : "border-border_color hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-black font-grotesk">
                        {reward.reward_type === "amount" ? `$${reward.amount}` : reward.gift_card_number}
                      </span>
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          reward.reward_type === "amount"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-purple-100 text-purple-600"
                        }`}
                      >
                        {reward.reward_type === "amount" ? "💵 Amount" : "🎁 Gift"}
                      </span>
                    </div>
                    {/* {reward.reward_type === "amount" && (
                      <span className="text-xs text-gray-500 mt-0.5 block">
                        Default: {reward.amount || reward.value}
                      </span>
                    )}
                    {reward.reward_type === "gift" && reward.description && (
                      <span className="text-xs text-gray-500 mt-0.5 block">
                        {reward.description}
                      </span>
                    )} */}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-400 py-4">No rewards available.</p>
            )}

            {/* Amount input — only for amount type reward */}
            {selectedReward?.reward_type === "amount" && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-black font-grotesk mb-1">
                  Reward Amount
                </label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full border border-border_color rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleCloseRewardPopup}
                className="flex-1 text-sm font-bold text-gray-600 border border-border_color font-grotesk py-3 rounded-[10px] hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReward}
                disabled={
                  !selectedReward ||
                  (selectedReward.type === "amount" && !customAmount) ||
                  rewardLoading
                }
                className="flex-1 text-sm font-bold text-white bg-primary font-grotesk py-3 rounded-[10px] hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {rewardLoading ? (
                  <>
                    <Spinner size={16} color="#fff" />
                    <span>Saving...</span>
                  </>
                ) : (
                  "Save Reward"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};